import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import i18n from "@me-team/host/src/i18n";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { CategoryInterface, TechMapsFilterI } from "../types";
import ErrorService from "../../../services/ErrorService";
import { useGetCategoryQuery } from "@me-team/host/main/services/graphql/services.hooks";
import transformCategories from "@me-team/host/src/utils/transformCategories";
import { Spinner } from "@services/ui-components";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";

interface ServicesFilterWrapperProps {
    setFilters: React.Dispatch<React.SetStateAction<TechMapsFilterI>>;
}

const TechMapServicesFilterWrapper: React.FC<ServicesFilterWrapperProps> = ({ setFilters }) => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const dropdownRef = useRef(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<CategoryInterface[]>(null);
    const [currentCatNameFilter, setCurrentCatNameFilter] = useState({
        categoryName: "",
        subCategoryName: "",
    });
    const [currentIdCatFilter, setCurrentIdCatFilter] = useState({
        categoryId: null,
        subCategoryId: null,
    });
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const { data: categoriesData, loading: categoriesLoading } = useGetCategoryQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            setSelectedCategory(transformCategories(data?.user?.company?.serviceCategoriesNew));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLInputElement;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                target.id !== "techMapServices-categoryId-field"
            ) {
                setIsPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCategoryNameChange = (categoryName: string) => {
        setCurrentCatNameFilter({ categoryName, subCategoryName: "" });
    };
    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string = "") => {
        setCurrentCatNameFilter({ categoryName, subCategoryName });
    };
    const handleCategoryIdChange = (categoryId: number) => {
        setCurrentIdCatFilter({ categoryId, subCategoryId: null });
    };
    const handleSubCategoryIdChange = (categoryId: number, subCategoryId: number) => {
        setCurrentIdCatFilter((prevState) => ({
            ...prevState,
            categoryId: categoryId,
            subCategoryId: subCategoryId,
        }));
    };

    const handleApplyFilter = () => {
        setFilters({ search: "", categoryId: null });
        setFilters({
            search: search ? search.trim() : "",
            categoryId: currentIdCatFilter?.subCategoryId
                ? currentIdCatFilter?.subCategoryId
                : currentIdCatFilter?.categoryId,
        });
        setIsPickerOpen(false);
    };

    const resetSelectedCategories = (categories: CategoryInterface[]) => {
        return categories?.map((category) => ({
            ...category,
            isPicked: false,
            isSubcategoryPiked: false,
            subcategoryList: category?.subcategoryList?.map((subcategory) => ({
                ...subcategory,
                isPicked: false,
            })),
        }));
    };

    const handleClearFilter = () => {
        setFilters({
            search: null,
            categoryId: null,
        });
        setCurrentCatNameFilter({ categoryName: "", subCategoryName: "" });
        setSearch("");
        setSelectedCategory(null);
        setCurrentIdCatFilter({ categoryId: null, subCategoryId: null });
        if (selectedCategory) {
            setSelectedCategory(resetSelectedCategories(selectedCategory));
        }
        setIsPickerOpen(false);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    if (categoriesLoading) return <Spinner />;

    return (
        <>
            <Row className="gx-3">
                <Col lg={8} xl={9}>
                    <Row className="gx-3 h-100">
                        <Col lg={6}>
                            <div className="position-relative mb-3 mb-lg-0">
                                <input
                                    name="search"
                                    id="techMapServices-search-field"
                                    type="text"
                                    className="form-control px-6 pe-4"
                                    placeholder={t("Search by name")}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value.trimStart())}
                                    onBlur={(e) => setSearch(e.target.value.trim())}
                                    onKeyDown={handleSearchKeyDown}
                                />
                                <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className="position-relative mb-3 mb-lg-0">
                                <input
                                    type="text"
                                    id="techMapServices-categoryId-field"
                                    className="form-control px-4 cursor-pointer"
                                    placeholder={t("category")}
                                    value={
                                        currentCatNameFilter?.categoryName || currentCatNameFilter?.subCategoryName
                                            ? `${currentCatNameFilter?.categoryName}  ${currentCatNameFilter?.subCategoryName}`
                                            : ""
                                    }
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsPickerOpen((prev) => !prev);
                                    }}
                                    readOnly
                                />
                                <i
                                    className={`bi bi-chevron-down position-absolute top-50 end-0 text-dark me-2 pointer-event-none transition-transform-icon ${isPickerOpen ? "rotate-180-icon" : ""}`}
                                ></i>
                                <div
                                    ref={dropdownRef}
                                    className="dropdown position-absolute opacity-1 z-2 top-10  start-0 end-0 mt-2 bg-white shadow"
                                >
                                    {categoriesData && selectedCategory && (
                                        <CategoryPickerComponent
                                            i18n={i18next}
                                            isOpen={isPickerOpen}
                                            onSearchChange={() => {}}
                                            onChangeCategory={(currentCategory: CategoryPickerCategory) => {
                                                if (currentCategory.isPicked) {
                                                    handleCategoryNameChange(currentCategory?.name);
                                                    handleCategoryIdChange(currentCategory?.id);
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentCatNameFilter({ categoryName: "", subCategoryName: "" });
                                                }
                                            }}
                                            onChangeSubcategory={(currentSubcategory, currentCategory) => {
                                                if (currentSubcategory?.isPicked) {
                                                    handleSubCategoryNameChange(currentSubcategory?.name);
                                                    handleSubCategoryIdChange(
                                                        currentCategory?.id,
                                                        currentSubcategory?.id
                                                    );
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentCatNameFilter({ categoryName: "", subCategoryName: "" });
                                                }
                                            }}
                                            serviceCategories={selectedCategory}
                                        />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
                <Col lg={4} xl={3}>
                    <Row className="gx-3 h-100">
                        <Col sm={6} className="mb-3 mb-lg-0">
                            <Button
                                type="button"
                                variant="outline-dark"
                                className="border-grey w-100 h-100 px-1 fw-normal"
                                onClick={handleClearFilter}
                            >
                                {t("clear")}
                            </Button>
                        </Col>
                        <Col sm={6} className="mb-3 mb-lg-0">
                            <Button
                                variant="primary"
                                className="w-100 h-100 px-1 fw-normal"
                                onClick={handleApplyFilter}
                            >
                                {t("apply")}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default TechMapServicesFilterWrapper;
